import client from '../client'

/**
 * Update the brand object
 * @param {string} brandId Id of brand being updated
 * @param {object} updatedSettings - updated brand settings
 * @returns {Promise} Resolves an updated brand object
 */
export const updateBrand = (brandId, updatedSettings) =>
  client.patch(`/brands/${brandId}`, updatedSettings)

/**
 * Fetches a brand object
 * @param {string} brandId ID of the brand you're fetching
 */
export const getBrand = (brandId) => client.get(`brands/${brandId}`)

/**
 * Updates brand SMS settings
 * @param {string} brandId ID of the brand you're fetching
 * @param {object} updatedSettings New settings
 */
export const updateBrandSMSMarketing = (brandId, updatedSettings) =>
  client.patch(`brands/${brandId}/sms-marketing`, updatedSettings)

/**
 * Fetches a paginated list of brands
 * @param {object} query A Brand-filtering query
 */
export const getBrands = (query) => client.get(`brands`, query)

/**
 * Creates a new brand
 * @param {object} payload A brand object
 * @returns {object} a saved brand object
 */
export const createBrand = (payload) => client.post('brands', payload)

/**
 * Links Google account to brand
 * @param {string} brandId ID of brand to link
 * @param {string} code Callback code from Google
 */
export const linkGoogleAccount = (brandId, code) =>
  client.post(`brands/${brandId}/google/link`, { code })

/**
 * Unlinks Google account from brand
 * @param {string} brandId ID of brand to link
 */
export const unlinkGoogleAccount = (brandId) =>
  client.delete(`brands/${brandId}/google`)

/**
 * Gets Google My Business properties
 * @param {string} brandId ID of brand
 */
export const getBrandDomain = async (brandId) => {
  const domains = await client.get(`brands/${brandId}/domain`)

  return domains.length ? domains : null
}

/**
 * Gets Google account info
 * @param {string} brandId Owner brand ID
 */
export const getGoogleAccountInfo = (brandId) =>
  client.get(`/brands/${brandId}/google`)

/**
 * Gets Google My Business properties
 * @param {string} brandId ID of brand
 */
export const getGoogleLocations = (brandId) =>
  client.get(`brands/${brandId}/google/locations`)

/**
 * Gets a brand's online health score & improvements
 * Note: the `score` is an average of the brand's location's scores
 * Each improvement is tagged to a specific location.
 *
 * Promise<{
 * score: number
 * improvements: {
 * improvementPotential: number
 * slug: string
 * points: number
 * locationId: string
 * }[]
 * }>
 * @param {string} brandId Owner brand id
 * @returns {Promise} score and improvements (see comment)
 */
export const getBrandOnlineHealth = (brandId) =>
  client.get(`/brands/${brandId}/online-health`)

/**
 * Gets available promotions associated with a brand
 * @param {string} brandId Owner brand id
 * @param {string} [locationId] Optional location id
 */
export const getActivePromotions = (brandId, locationId = undefined) =>
  client.get('/coupons', {
    available: true,
    brandId,
    locationId,
    promotional: true,
  })

/**
 * Sets up SMS marketing for a brand
 * @param {string} brandId Owner brand id to setup
 */
export const setupSMSMarketing = (brandId) =>
  client.put(`/brands/${brandId}/sms-marketing/setup`, { brandId })

/**
 * Updates brand links
 * @param {string} brandId Id of brand to change
 * @param {object} links Content to send to the server
 * @param {boolean} links.olo external OLO link
 * @param {boolean} links.reservations external reservations link
 */
export const updateLinks = (brandId, links) =>
  client.put(`brands/${brandId}/links`, links)

/**
 * Updates Brand Assets
 * @param {string} brandId Id of brand to change
 * @param {object} assets Assets Object
 * @param {string|null} assets.logo Logo
 * @param {string|null} assets.bannerImage Banner Image
 */
export const updateBrandAssets = (brandId, assets) =>
  client.patch(`brands/${brandId}/`, { assets })

/**
 * Gets a brand theme
 * Note - this is for the unmerged brand theme on Hermes only
 * @param {string} brandId Owner brand id
 */
export const getBrandTheme = (brandId) =>
  client.get(`brands/${brandId}/theme/brand`)

/**
 * Updates selected brightness palette for a brand
 * @param {string} brandId Owner brand id
 * @param {string} palette Selected palette `light | dark`
 */
export const updateBrightnessPalette = (brandId, palette) =>
  client.patch(`brands/${brandId}/theme/overrides/palette`, { palette })

/**
 * Updates brand theme overrides
 * @param {string} brandId Owner brand id
 * @param {object} overrides Brand theme overrides
 */
export const updateThemeOverrides = (brandId, overrides) =>
  client.patch(`brands/${brandId}/theme/overrides`, overrides)
